/**
 * BankID helpers for pwa-atkomst
 * Mirrors the exact same patterns as pwa-handel (openBankId.ts + bankid-api.ts)
 * Uses the same api-auth Edge Function endpoints via Redlight Media.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

/** Base URL for Edge Function calls */
export const FUNCTIONS_BASE = `${supabaseUrl}/functions/v1`;

/**
 * Standard headers for BankID API calls (init, verify-only, create-user).
 * Pre-login calls use the anon key as both apikey and bearer token.
 */
export function getBankIdHeaders(accessToken?: string): Record<string, string> {
  return {
    'content-type': 'application/json',
    'apikey': supabaseAnonKey,
    'authorization': `Bearer ${accessToken ?? supabaseAnonKey}`,
  };
}

/**
 * Build correct BankID URL based on platform (mobile vs desktop).
 * Exact same logic as pwa-handel.
 */
export function buildBankIdUrl(autoStartToken: string, rawRedirectUrl: string): string {
  const ua = navigator.userAgent || '';
  const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
  const redirect = encodeURIComponent(rawRedirectUrl);

  return isMobile
    ? `bankid:///?autostarttoken=${autoStartToken}&redirect=${redirect}`
    : `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${redirect}`;
}

/**
 * BankID deeplink opener — simplified to prevent duplicate dialogs on Android.
 * Exact same logic as pwa-handel.
 */
export function openBankIdDeepLink(url: string): void {
  let opened = false;

  // Primary: anchor click (most reliable on mobile, preserves user gesture)
  try {
    const a = document.createElement('a');
    a.href = url;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    opened = true;
  } catch {
    // Fallback below
  }

  // Fallback: location.href ONLY if anchor failed
  if (!opened) {
    try {
      window.location.href = url;
    } catch {
      // Nothing more we can do
    }
  }
}

export interface BankIdSessionTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Starta ett BankID-flöde: init mot api-auth, spara orderRef för
 * callback-sidan och öppna BankID-appen. Delad av inloggningen (LoginPage)
 * och kontokopplingen (LoginMethodsCard) — enda skillnaden är link-läget,
 * som callback-sidan läser via mode=link + sessionStorage-flaggan.
 * Returnerar felmeddelande i stället för att kasta (anropas från UI-handlers).
 */
export async function startBankIdFlow(
  options: { linkMode?: boolean } = {}
): Promise<{ ok: true } | { ok: false; message: string }> {
  sessionStorage.removeItem('bankid_orderRef');
  sessionStorage.removeItem('bankid_orderRef_timestamp');
  sessionStorage.removeItem('bankid_link_mode');

  try {
    const response = await fetch(`${FUNCTIONS_BASE}/api-auth/bankid/init`, {
      method: 'POST',
      headers: getBankIdHeaders(),
      body: JSON.stringify({}),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.orderRef || !data?.autoStartToken) {
      return { ok: false, message: data?.message || 'Kunde inte starta BankID' };
    }

    sessionStorage.setItem('bankid_orderRef', data.orderRef);
    sessionStorage.setItem('bankid_orderRef_timestamp', Date.now().toString());
    if (options.linkMode) {
      sessionStorage.setItem('bankid_link_mode', 'true');
    }

    const modeParam = options.linkMode ? 'mode=link&' : '';
    const rawRedirectUrl = `${window.location.origin}/bankid-callback?${modeParam}oref=${data.orderRef}`;
    openBankIdDeepLink(buildBankIdUrl(data.autoStartToken, rawRedirectUrl));
    return { ok: true };
  } catch {
    return { ok: false, message: 'Ett oväntat fel uppstod vid BankID-start' };
  }
}

/**
 * Skapa konto + Supabase-session för en NY BankID-verifierad användare via
 * /bankid/create-user. Personnummer-inbjudna användare existerar inte förrän
 * första BankID-inloggningen, så callback-flödet MÅSTE kunna skapa kontot.
 * Endpointen är idempotent: finns kontot redan (race) returneras session för
 * det befintliga kontot. Kastar Error med användbart meddelande vid fel.
 */
export async function createBankIdUser(
  verificationToken: string,
  displayName?: string
): Promise<BankIdSessionTokens> {
  const response = await fetch(`${FUNCTIONS_BASE}/api-auth/bankid/create-user`, {
    method: 'POST',
    headers: getBankIdHeaders(),
    body: JSON.stringify({
      verification_token: verificationToken,
      display_name: displayName,
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok || !data?.supabase_access_token || !data?.supabase_refresh_token) {
    throw new Error(data?.error || 'Kunde inte skapa konto');
  }

  return {
    accessToken: data.supabase_access_token,
    refreshToken: data.supabase_refresh_token,
  };
}

/**
 * Koppla en färsk BankID-verifiering (verification_token från verify-only)
 * till det INLOGGADE kontot via api-bankid-link. Kräver användarens
 * access token — länkning sker alltid mot det egna kontot, aldrig via
 * kontobyte. Kastar Error med serverns meddelande vid konflikt/fel
 * (t.ex. identitet som redan tillhör ett annat konto).
 */
export async function linkBankIdVerification(
  verificationToken: string,
  accessToken: string
): Promise<void> {
  const response = await fetch(`${FUNCTIONS_BASE}/api-bankid-link`, {
    method: 'POST',
    headers: getBankIdHeaders(accessToken),
    body: JSON.stringify({ verification_token: verificationToken }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.error || 'Kunde inte koppla BankID');
  }
}

/**
 * Swedish error messages for BankID hint codes.
 */
export const BANKID_ERROR_MESSAGES: Record<string, string> = {
  userCancel: 'Du avbröt BankID-signeringen',
  cancelled: 'BankID-sessionen avbröts',
  cancelledByUser: 'Du avbröt BankID-signeringen',
  expired: 'BankID-sessionen har gått ut. Försök igen.',
  expiredTransaction: 'BankID-sessionen har gått ut. Försök igen.',
  timeout: 'Tidsgräns uppnådd. Försök igen.',
  technical_error: 'Ett tekniskt fel uppstod. Försök igen.',
  startFailed: 'Kunde inte starta BankID. Kontrollera att appen är installerad.',
  certificateErr: 'Problem med BankID-certifikatet. Kontakta support.',
  alreadyInProgress: 'BankID används redan. Vänta eller avbryt i appen.',
  internalError: 'BankID-tjänsten har problem. Försök igen senare.',
  noSuchOrder: 'BankID-sessionen har gått ut. Försök igen.',
  invalidParameters: 'BankID-sessionen har gått ut. Försök igen.',
};

export function getErrorMessage(hintCode?: string, rawMessage?: string): string {
  if (hintCode && BANKID_ERROR_MESSAGES[hintCode]) {
    return BANKID_ERROR_MESSAGES[hintCode];
  }
  if (rawMessage?.toLowerCase().includes('no such order')) {
    return BANKID_ERROR_MESSAGES.noSuchOrder;
  }
  return 'BankID misslyckades. Försök igen.';
}
